import {
  ColumnDefWithFilters,
  ColumnGroup,
  EnrichedTeamMembership,
} from '../types'
import {
  computeRankById,
  gearStats,
  statPercentageToColor,
  useSimpleColumns,
} from '../utils'
import { useAudit } from '../context'
import {
  itemEquipmentSlots,
  itemSlotLabels,
} from 'app/frontend/types/custom/ItemSlot'
import { useMemo } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { wowheadUrl } from 'app/frontend/base/helpers'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select from 'app/frontend/base/components/Select'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import {
  colorByTrack,
  upgradeTracks,
} from 'app/frontend/types/custom/UpgradeTrack'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Check, Info } from 'lucide-react'
import { getSeasonData } from 'app/frontend/base/services/seasonData'
import dayjs from 'dayjs'

const tierSlots = ['head', 'shoulder', 'chest', 'hands', 'legs'] as const

const formatBonusRollAge = (value: string | number) => {
  if (value === undefined || value === null || value === '') return '-'
  const created = dayjs.unix(Number(value))
  const diffInHours = dayjs().diff(created, 'hour')
  if (diffInHours < 24) return `${diffInHours}h`
  return `${dayjs().diff(created, 'day')}d`
}

export const ilvlDisplayOptions = [
  { label: 'Highest ever', value: 'best' },
  { label: 'Current', value: 'current' },
]

export const slotDisplayOptions = [
  { label: 'Name', value: 'name' },
  { label: 'Item level', value: 'ilvl' },
  { label: 'Upgrade track', value: 'track' },
]

export const useGearOverviewColumns = (): ColumnDefWithFilters => {
  const {
    data: { gameVersion, currentSeason },
  } = usePageInfo()

  const availableSlotDisplayOptions = slotDisplayOptions.filter(
    (option) => option.value !== 'track' || gameVersion === 'live',
  )

  const { value: slotDisplay, setValue: setSlotDisplay } = useAuditFilterState<
    'name' | 'ilvl' | 'track'
  >(
    'slotDisplay',
    'ilvl',
    availableSlotDisplayOptions.map((o) => o.value),
  )
  const { value: totalIlvlDisplay, setValue: setTotalIlvlDisplay } =
    useAuditFilterState<'best' | 'current'>(
      'totalIlvlDisplay',
      'best',
      ilvlDisplayOptions.map((o) => o.value),
    )

  const { visibleMembers } = useAudit()

  const tierId = getSeasonData(gameVersion).find(
    (season) => season.id === currentSeason.id,
  )?.tier_id

  const tierRankById = useMemo(
    () =>
      gameVersion === 'live'
        ? undefined
        : computeRankById(
            visibleMembers,
            (member) =>
              tierSlots.filter(
                (slot) => member.data?.[`tier_${tierId}_${slot}`] === 'yes',
              ).length,
          ),
    [gameVersion, tierId, visibleMembers],
  )

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'item_level',
        header: '',
        subColumns: [
          {
            id: 'total_ilvl',
            header: 'Item level',
            numeric: true,
            field:
              totalIlvlDisplay === 'best'
                ? 'highest_ilvl_ever_equipped'
                : 'ilvl',
            cellWrapper: (value) => {
              return <span>{value ? Number(value).toFixed(2) : '-'}</span>
            },
          },
          ...(gameVersion === 'live'
            ? [
                {
                  id: 'sparks_used',
                  header: 'Sparks used',
                  field: 'ingenuity_sparks_equipped',
                  cellWrapper: (
                    value: string | number,
                    member: EnrichedTeamMembership,
                  ) => {
                    const seasonSparkGear =
                      member.sparkGear?.[String(currentSeason.id)]

                    const sparkSlots = itemEquipmentSlots
                      .filter((slot) => seasonSparkGear?.[slot]?.id)
                      // Items crafted before the spark cost was tracked count as one
                      .map((slot) => ({
                        label: itemSlotLabels[slot],
                        sparks: seasonSparkGear?.[slot]?.sparks ?? 1,
                      }))

                    const sparksSpent = sparkSlots.reduce(
                      (total, slot) => total + slot.sparks,
                      0,
                    )

                    return (
                      <Tooltip>
                        <TooltipTrigger>
                          {Math.max(Number(value) || 0, sparksSpent)}
                        </TooltipTrigger>
                        {sparkSlots.length > 0 ? (
                          <TooltipContent>
                            {sparkSlots
                              .map((slot) =>
                                slot.sparks > 1
                                  ? `${slot.label} (${slot.sparks})`
                                  : slot.label,
                              )
                              .join(', ')}
                          </TooltipContent>
                        ) : null}
                      </Tooltip>
                    )
                  },
                },
              ]
            : []),
        ],
      },
      {
        id: 'tier',
        header:
          tierId && gameVersion !== 'live'
            ? `Tier ${tierId} items obtained`
            : 'Tier items obtained',
        rankFields: tierSlots.map((slot) =>
          gameVersion === 'live'
            ? `tier_${slot}_ilvl`
            : `tier_${tierId}_${slot}`,
        ),
        rankById: tierRankById,
        subColumns: [
          {
            id: 'set',
            header: 'Set',
            cellWrapper: (_, member) => {
              const slotsObtained = tierSlots.filter((slot) =>
                gameVersion === 'live'
                  ? Number(member.data?.[`tier_${slot}_ilvl`]) > 0
                  : member.data?.[`tier_${tierId}_${slot}`] === 'yes',
              ).length

              return (
                <span
                  className={cn(
                    'whitespace-nowrap',
                    slotsObtained >= 4
                      ? 'text-success-foreground'
                      : slotsObtained >= 2
                      ? 'text-warning-foreground'
                      : 'text-destructive-foreground',
                  )}
                >
                  {slotsObtained} / {tierSlots.length}
                </span>
              )
            },
          },
          ...tierSlots.map((slot) => ({
            id: `tier_${slot}`,
            header: () => {
              const label = itemSlotLabels[slot]
              return (
                <Tooltip>
                  <TooltipTrigger className='uppercase'>
                    {label[0]}
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              )
            },
            field:
              gameVersion === 'live'
                ? `tier_${slot}_difficulty`
                : `tier_${tierId}_${slot}`,
            cellWrapper: (value: string | number) => {
              if (gameVersion !== 'live') {
                return value === 'yes' ? (
                  <Check className='w-4 h-4 text-success-foreground' />
                ) : null
              }

              const fullTrack = upgradeTracks.find(
                (track) => track[0] === value,
              )
              return (
                <span className={cn(fullTrack ? colorByTrack[fullTrack] : '')}>
                  {value}
                </span>
              )
            },
          })),
        ],
      },
      ...(gameVersion !== 'live'
        ? []
        : [
            {
              id: 'bonus_rolls',
              header: () => (
                <span>
                  Bonus rolls
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Info className='inline-block align-middle ml-2 mb-0.5 w-3 h-3 text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-80 text-center'>
                      Bonus roll data is collected by the WowauditCompanion
                      addon and uploaded by the desktop client. Only data of
                      online characters with either the WowauditCompanion or
                      RCLootCouncil_wowaudit addon installed is synced.
                    </TooltipContent>
                  </Tooltip>
                </span>
              ),
              rankFields: null,
              subColumns: [
                {
                  id: 'bonus_roll_left',
                  header: 'Left',
                  field: 'bonus_roll_left',
                  numeric: true,
                  cellWrapper: (value: string | number) => (
                    <span>{value ?? '-'}</span>
                  ),
                },
                {
                  id: 'bonus_roll_earned',
                  header: 'Earned',
                  field: 'bonus_roll_earned',
                  numeric: true,
                  cellWrapper: (value: string | number) => (
                    <span>{value ?? '-'}</span>
                  ),
                },
                {
                  id: 'bonus_roll_age',
                  header: 'Age',
                  field: 'bonus_roll_updated_at',
                  numeric: true,
                  cellWrapper: (value: string | number) => (
                    <span>{formatBonusRollAge(value)}</span>
                  ),
                },
              ],
            },
          ]),
      ...(gameVersion !== 'live'
        ? []
        : [
            {
              id: 'stat_distribution',
              header: 'Gear stat distribution (%)',
              rankFields: null,
              subColumns: gearStats.map((stat) => ({
                id: stat.value,
                header: () => (
                  <Tooltip>
                    <TooltipTrigger>{stat.label}</TooltipTrigger>
                    <TooltipContent>{stat.fullLabel}</TooltipContent>
                  </Tooltip>
                ),
                field: `${stat.value}_gear_percentage`,
                cellWrapper: (value: string | number) => (
                  <span className={cn(statPercentageToColor(Number(value)))}>
                    {value}
                  </span>
                ),
              })),
            },
          ]),
      {
        id: 'equipped',
        header: 'Equipped gear',
        rankFields: null,
        subColumns: itemEquipmentSlots.map((slot) => ({
          id: `equipped_${slot}`,
          header: () => <span>{itemSlotLabels[slot]}</span>,
          field: slotDisplay === 'ilvl' ? `${slot}_ilvl` : `${slot}_name`,
          cellWrapper: (_, member) => {
            const item = member.gear?.[slot]

            return item?.id ? (
              <a
                href={wowheadUrl(gameVersion, member.gear?.[slot])}
                target='_blank'
                rel='noreferrer'
                className={`whitespace-nowrap font-semibold inline-flex gap-2 items-center ${
                  slotDisplay === 'track'
                    ? colorByTrack[item.upgrade_level?.track || 'None']
                    : itemQualityClasses[item.quality || 0]
                }`}
              >
                <ItemIcon id={item.id} />
                <span>
                  {slotDisplay === 'name'
                    ? item.name
                    : slotDisplay === 'ilvl'
                    ? item.ilvl
                    : item.upgrade_level?.track}
                </span>
              </a>
            ) : null
          },
        })),
      },
    ],
    [
      currentSeason.id,
      gameVersion,
      tierId,
      tierRankById,
      slotDisplay,
      totalIlvlDisplay,
    ],
  )

  const columns = useSimpleColumns(collectionGroups, [
    slotDisplay,
    totalIlvlDisplay,
  ])

  return {
    columns,
    filters: [
      <AuditFilter label='Shown item level' key='totalIlvlDisplay'>
        <Select
          className='w-40'
          value={totalIlvlDisplay}
          setValue={(value) => setTotalIlvlDisplay(value as 'best' | 'current')}
          options={ilvlDisplayOptions}
        />
      </AuditFilter>,
      <AuditFilter label='Gear display' key='slotDisplay'>
        <Select
          className='w-40'
          value={slotDisplay}
          setValue={(value) =>
            setSlotDisplay(value as 'name' | 'ilvl' | 'track')
          }
          options={availableSlotDisplayOptions}
        />
      </AuditFilter>,
    ],
    initialSortBy: [{ id: 'total_ilvl', desc: true }],
  }
}
